import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect, getSpriteData } from '../../entityTypes';
import { CanvasSprite } from './SpriteNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Shapes, ToggleLeft, ToggleRight, Layers, Trash2, MousePointer2 } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const SPRITE_SIZE_OPTIONS = [
  { id: '32', label: 'S (32)' },
  { id: '64', label: 'M (64)' },
  { id: '128', label: 'L (128)' },
];

const SPRITE_STROKE_WIDTH_OPTIONS = [
  { id: '0.5', label: '0.5px' },
  { id: '1', label: '1px' },
  { id: '1.5', label: '1.5px' },
  { id: '2', label: '2px' },
  { id: '3', label: '3px' },
  { id: '4', label: '4px' },
  { id: '6', label: '6px' },
  { id: '8', label: '8px' },
];

const SPRITE_COLOR_OPTIONS = [
  { id: 'dark', label: t('canvasEntity.colorDark'), color: '#374151' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#6b7280' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#2563eb' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#16a34a' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#dc2626' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: '#7c3aed' },
  { id: 'orange', label: t('canvasEntity.colorOrange'), color: '#ea580c' },
];

export class SpriteHandler extends EntityKindHandler {
  readonly kind = 'sprite';
  readonly Component = CanvasSprite;
  readonly dataAttribute = 'data-canvas-sprite';
  canConnect = true;
  canResize = true;

  getAddPickerEntry(): AddPickerEntry {
    return { label: 'Sprite', icon: Shapes, iconColor: '#8b5cf6', priority: 50, mode: 'instant' };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (!entity.connectable) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const d = getSpriteData(entity);
    const actions: ToolbarAction[] = [
      {
        name: 'color',
        label: t('canvasEntity.color'),
        type: 'color',
        options: SPRITE_COLOR_OPTIONS,
        getValue: () => d.color,
        apply: (v) => store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { color: v as string } }),
      },
      {
        name: 'size',
        label: t('canvasEntity.size'),
        type: 'select',
        options: SPRITE_SIZE_OPTIONS,
        getValue: () => {
          const w = entity.width;
          const match = SPRITE_SIZE_OPTIONS.find(o => Number(o.id) === w);
          return match ? match.id : String(w);
        },
        apply: (v) => {
          const size = Number(v);
          store.send({ type: 'entity.resize', entityId: entity.id, x: entity.x, y: entity.y, width: size, height: size });
        },
      },
    ];

    if (d.fillMode === 'stroke') {
      actions.push({
        name: 'strokeWidth',
        label: t('canvasEntity.strokeWidth'),
        type: 'select',
        options: SPRITE_STROKE_WIDTH_OPTIONS,
        getValue: () => String(d.strokeWidth ?? 2),
        apply: (v) => store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { strokeWidth: Number(v) } }),
        allowCustomInput: true,
        customInputUnit: 'px',
        customInputMin: 0.5,
        customInputMax: 20,
        customInputStep: 0.5,
      });

      actions.push({
        name: 'fixedStroke',
        label: t('canvasEntity.fixedStroke'),
        type: 'toggle',
        getValue: () => !!d.fixedStroke,
        apply: (v) => store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { fixedStroke: !!v } }),
      });
    }

    actions.push({
      name: 'borderColor',
      label: t('canvasEntity.borderColor'),
      type: 'color',
      options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
      getValue: () => d.borderColor ?? '',
      apply: (v) => {
        const color = v as string;
        if (!color) {
          store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
        } else {
          store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
        }
      },
    });

    actions.push({
      name: 'borderWidth',
      label: t('canvasEntity.borderWidth'),
      type: 'select',
      options: BORDER_WIDTH_OPTIONS,
      getValue: () => d.borderColor ? String(d.borderWidth ?? 1) : 'none',
      apply: (v) => {
        if (v === 'none') {
          store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
        } else {
          store.send({ type: 'sprite.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
        }
      },
    });

    actions.push({
      name: 'connectable',
      label: 'Connectors',
      type: 'toggle',
      getValue: () => entity.connectable,
      apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
    });

    actions.push({
      name: 'clickThrough',
      label: 'Click-Through',
      type: 'select',
      options: [
        { id: 'auto', label: 'Auto' },
        { id: 'always', label: t('canvasEntity.ctAlways') },
        { id: 'never', label: t('canvasEntity.ctNever') },
      ],
      getValue: () => d.clickThrough ?? 'auto',
      apply: (v) => store.send({
        type: 'sprite.updateData',
        entityId: entity.id,
        dataChanges: { clickThrough: v === 'auto' ? undefined : v as 'always' | 'never' },
      }),
    });

    return actions;
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const spriteId = entity.id;
    const d = getSpriteData(entity);
    const items: ContextMenuItem[] = [];

    // --- Pos. 2: Color submenu ---
    items.push(buildColorGridSubmenu({
      id: 'sprite-color-submenu',
      label: t('canvasEntity.color'),
      currentColor: d.color,
      handlerColors: [...SPRITE_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { color } }),
    }));

    // --- Pos. 2: Size submenu ---
    const sizeOptions = [
      { id: 's', label: 'S (32)', size: 32 },
      { id: 'm', label: 'M (64)', size: 64 },
      { id: 'l', label: 'L (128)', size: 128 },
    ];
    items.push({
      id: 'sprite-size-submenu',
      label: t('canvasEntity.size'),
      children: sizeOptions.map(opt => ({
        id: `sz-${opt.id}`,
        label: `${entity.width === opt.size ? '● ' : ''}${opt.label}`,
        onClick: () => store.send({ type: 'entity.resize', entityId: spriteId, x: entity.x, y: entity.y, width: opt.size, height: opt.size }),
      })),
    });

    // --- Pos. 2: Stroke width submenu (only for stroke-mode sprites) ---
    if (d.fillMode === 'stroke') {
      const currentStrokeWidth = d.strokeWidth ?? 2;
      const strokeOptions = [
        { id: 'sw-1', label: t('canvasEntity.strokeThin'), value: 1 },
        { id: 'sw-1.5', label: t('canvasEntity.strokeLight'), value: 1.5 },
        { id: 'sw-2', label: t('canvasEntity.strokeNormal'), value: 2 },
        { id: 'sw-3', label: t('canvasEntity.strokeMedium'), value: 3 },
        { id: 'sw-4', label: t('canvasEntity.strokeThick'), value: 4 },
      ];
      items.push({
        id: 'sprite-strokewidth-submenu',
        label: t('canvasEntity.strokeWidth'),
        children: [
          ...strokeOptions.map(opt => ({
            id: opt.id,
            label: `${currentStrokeWidth === opt.value ? '● ' : ''}${opt.label}`,
            onClick: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { strokeWidth: opt.value } }),
          })),
          {
            id: 'sw-custom',
            label: t('canvasEntity.customValue'),
            separator: true,
            inlineNumberInput: {
              currentValue: currentStrokeWidth,
              onChangeValue: (value: number) => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { strokeWidth: value } }),
              min: 0.5,
              max: 20,
              step: 0.5,
              unit: 'px',
            },
          },
        ],
      });

      // --- Pos. 2b: Fixed stroke toggle ---
      items.push({
        id: 'toggle-fixed-stroke',
        label: d.fixedStroke ? t('canvasEntity.strokeScaling') : t('canvasEntity.strokeFixed'),
        icon: d.fixedStroke ? ToggleRight : ToggleLeft,
        onClick: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { fixedStroke: !d.fixedStroke } }),
      });
    }

    // --- Border color submenu ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: d.borderColor,
      onSelectColor: (color) => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Pos. 3: Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      separator: true,
      onClick: () => store.send({ type: 'entity.setConnectable', entityId: spriteId, connectable: !entity.connectable }),
    });

    // --- Pos. 3b: Click-Through submenu ---
    const currentCT = d.clickThrough ?? 'auto';
    items.push({
      id: 'sprite-clickthrough-submenu',
      label: 'Click-Through',
      icon: MousePointer2,
      iconColor: '#f59e0b',
      children: [
        { id: 'ct-auto', label: currentCT === 'auto' ? '● Auto' : 'Auto',
          onClick: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { clickThrough: undefined } }) },
        { id: 'ct-always', label: currentCT === 'always' ? `● ${t('canvasEntity.ctAlways')}` : t('canvasEntity.ctAlways'),
          onClick: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { clickThrough: 'always' } }) },
        { id: 'ct-never', label: currentCT === 'never' ? `● ${t('canvasEntity.ctNever')}` : t('canvasEntity.ctNever'),
          onClick: () => store.send({ type: 'sprite.updateData', entityId: spriteId, dataChanges: { clickThrough: 'never' } }) },
      ],
    });

    // --- Pos. 4: Gruppe submenu ---
    items.push({ ...buildGroupSubmenu(ctx), separator: true });

    // --- Pos. 5: Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId: spriteId, layerId });
      }),
    });

    // --- Pos. 7: Remove ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.remove'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: spriteId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Pos. 8: Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.elements'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

}
